import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit2, FolderTree, MoreHorizontal, Loader2, Trash2,
  ChevronDown, ChevronRight, FolderOpen, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { TablePagination } from '@/components/TablePagination';
import { ImageUpload } from '@/components/ImageUpload';
import { API_URL } from '@/lib/api';

/* ── Confirm Delete Dialog ───────────────────────────────── */
const ConfirmDeleteDialog = ({ open, onClose, onConfirm, name, isDeleting, hasChildren }) => (
  <Dialog open={open} onOpenChange={onClose}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-rose-600">
          <AlertTriangle className="w-5 h-5" /> Delete Category
        </DialogTitle>
      </DialogHeader>
      <div className="py-3 space-y-2">
        <p className="text-sm text-slate-700">
          Are you sure you want to delete <strong>{name}</strong>?
        </p>
        {hasChildren ? (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            ⚠ This category has subcategories — they will also be deleted.
            Products will become uncategorised (not deleted).
          </p>
        ) : (
          <p className="text-xs text-slate-500">Products will become uncategorised (not deleted).</p>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          onClick={onConfirm} disabled={isDeleting}
          className="bg-rose-600 hover:bg-rose-700 text-white"
        >
          {isDeleting && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
          <Trash2 className="w-4 h-4 mr-1" /> Delete
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

/* ── Main Page ───────────────────────────────────────────── */
const Categories = () => {
  const [allCats,       setAllCats]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [expanded,      setExpanded]      = useState({});
  const [page,          setPage]          = useState(1);
  const [pageSize,      setPageSize]      = useState(10);
  const [modalOpen,     setModalOpen]     = useState(false);
  const [editCat,       setEditCat]       = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [formData,      setFormData]      = useState({ name: '', image_url: '', status: 'active', parentId: '' });
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [isDeleting,    setIsDeleting]    = useState(false);

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setAllCats((res.data || []).map(c => ({
        id:           c.id,
        name:         c.name,
        image_url:    c.image,
        status:       c.status === true ? 'active' : 'inactive',
        parentId:     c.parentId || null,
        productCount: c.productCount ?? 0,
        createdAt:    c.createdAt,
      })));
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const parents   = allCats.filter(c => !c.parentId);
  const subsOf    = (pid) => allCats.filter(c => c.parentId === pid);
  const paged     = parents.slice((page - 1) * pageSize, page * pageSize);

  const openModal = (cat = null, defaultParentId = '') => {
    if (cat) {
      setEditCat(cat);
      setFormData({ name: cat.name, image_url: cat.image_url || '', status: cat.status, parentId: cat.parentId || '' });
    } else {
      setEditCat(null);
      setFormData({ name: '', image_url: '', status: 'active', parentId: defaultParentId });
    }
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setEditCat(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name:   formData.name,
        image:  formData.image_url || null,
        status: formData.status === 'active',
        ...(formData.parentId ? { parentId: formData.parentId } : {}),
      };
      if (editCat) {
        await axios.put(`${API_URL}/categories/${editCat.id}`, payload);
        toast.success('Category updated');
      } else {
        await axios.post(`${API_URL}/categories`, payload);
        toast.success(`${formData.parentId ? 'Subcategory' : 'Category'} created`);
      }
      closeModal();
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/categories/${deleteTarget.id}`);
      toast.success('Category deleted');
      setDeleteTarget(null);
      fetchCategories();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggle = async (cat) => {
    try {
      await axios.put(`${API_URL}/categories/${cat.id}`, { status: cat.status !== 'active' });
      toast.success('Status updated');
      fetchCategories();
    } catch {
      toast.error('Failed to update status');
    }
  };

  return (
    <div data-testid="categories-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Categories</h1>
          <p className="text-slate-500 mt-1">Manage categories &amp; subcategories</p>
        </div>
        <Button onClick={() => openModal()} className="bg-purple-950 hover:bg-purple-900">
          <Plus className="w-4 h-4 mr-2" /> Add Category
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-4 space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center gap-4" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="w-10 h-10 rounded-xl shimmer flex-shrink-0" />
                <div className="flex-1 h-3.5 rounded shimmer" />
                <div className="w-16 h-6 rounded-full shimmer" />
                <div className="w-8 h-8 rounded shimmer" />
              </div>
            ))}
          </div>
        ) : parents.length === 0 ? (
          <div className="empty-state">
            <div className="w-16 h-16 rounded-2xl bg-purple-50 flex items-center justify-center mb-4">
              <FolderTree className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-base font-semibold text-slate-700">No categories yet</p>
            <p className="text-sm text-slate-400 mt-1">Create your first category to get started</p>
          </div>
        ) : (
          <>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="w-10" />
                <TableHead>Category</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>Subs</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paged.map((cat) => {
                const subs = subsOf(cat.id);
                const isOpen = !!expanded[cat.id];
                return (
                  <>
                  <TableRow key={cat.id} className="table-row-hover" data-testid={`category-row-${cat.id}`}>
                    <TableCell>
                      {subs.length > 0 && (
                        <button onClick={() => setExpanded(p => ({ ...p, [cat.id]: !p[cat.id] }))}
                          className="p-1 rounded hover:bg-slate-100 transition-colors">
                          {isOpen
                            ? <ChevronDown className="w-4 h-4 text-slate-500" />
                            : <ChevronRight className="w-4 h-4 text-slate-500" />}
                        </button>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {cat.image_url
                          ? <img src={cat.image_url} alt={cat.name} className="w-10 h-10 rounded-lg object-cover" />
                          : <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                              <FolderOpen className="w-5 h-5 text-purple-600" /></div>
                        }
                        <span className="font-semibold">{cat.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-100 px-2 py-0.5 rounded-full">
                        Parent
                      </span>
                    </TableCell>
                    <TableCell className="text-slate-500">{cat.productCount}</TableCell>
                    <TableCell className="text-slate-500">{subs.length}</TableCell>
                    <TableCell><Badge className={`status-${cat.status}`}>{cat.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openModal(cat)}>
                            <Edit2 className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openModal(null, cat.id)}>
                            <Plus className="w-4 h-4 mr-2" /> Add Subcategory
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggle(cat)}>
                            {cat.status === 'active' ? 'Disable' : 'Enable'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => setDeleteTarget({ ...cat, hasChildren: subs.length > 0 })}
                            className="text-rose-600 focus:text-rose-600 focus:bg-rose-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>

                  {/* Subcategory rows */}
                  {isOpen && subs.map((sub) => (
                    <TableRow key={sub.id} className="bg-purple-50/40 border-l-4 border-l-purple-200" data-testid={`subcat-row-${sub.id}`}>
                      <TableCell />
                      <TableCell>
                        <div className="flex items-center gap-3 pl-4">
                          <div className="w-0.5 h-6 rounded-full bg-purple-300 flex-shrink-0" />
                          {sub.image_url
                            ? <img src={sub.image_url} alt={sub.name} className="w-8 h-8 rounded-lg object-cover" />
                            : <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                <FolderTree className="w-4 h-4 text-purple-500" /></div>
                          }
                          <span className="font-medium text-slate-700">{sub.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                          Sub
                        </span>
                      </TableCell>
                      <TableCell className="text-slate-500">{sub.productCount}</TableCell>
                      <TableCell className="text-slate-400">—</TableCell>
                      <TableCell><Badge className={`status-${sub.status}`}>{sub.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openModal(sub)}>
                              <Edit2 className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleToggle(sub)}>
                              {sub.status === 'active' ? 'Disable' : 'Enable'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeleteTarget({ ...sub, hasChildren: false })}
                              className="text-rose-600 focus:text-rose-600 focus:bg-rose-50"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  </>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            data={parents} page={page} pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(v) => { setPageSize(v); setPage(1); }}
          />
          </>
        )}
      </div>

      {/* Create / Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={closeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editCat ? 'Edit Category' : formData.parentId ? 'Add Subcategory' : 'Create Category'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 py-4">
            {!editCat && (
              <div>
                <Label className="form-label">Category Type</Label>
                <Select
                  value={formData.parentId || 'none'}
                  onValueChange={(v) => setFormData(p => ({ ...p, parentId: v === 'none' ? '' : v }))}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Top-level Category</SelectItem>
                    {parents.map(c => (
                      <SelectItem key={c.id} value={c.id}>↳ Subcategory of: {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label htmlFor="cat-name" className="form-label">Name *</Label>
              <Input
                id="cat-name" value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="Category name" required data-testid="category-name-input"
              />
            </div>
            <div data-testid="category-image-input">
              <ImageUpload
                value={formData.image_url}
                onChange={(url) => setFormData(p => ({ ...p, image_url: url }))}
                folder="categories" label="Image" placeholder="Upload or paste URL"
              />
            </div>
            {editCat && (
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-sm">Active</p>
                  <p className="text-xs text-slate-500">Show in store</p>
                </div>
                <Switch
                  checked={formData.status === 'active'}
                  onCheckedChange={(c) => setFormData(p => ({ ...p, status: c ? 'active' : 'inactive' }))}
                  data-testid="category-status-toggle"
                />
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
              <Button type="submit" className="bg-purple-950 hover:bg-purple-900" disabled={saving} data-testid="save-category-btn">
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                {editCat ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        name={deleteTarget?.name}
        isDeleting={isDeleting}
        hasChildren={deleteTarget?.hasChildren}
      />
    </div>
  );
};

export default Categories;
